/**
 * View Only screen visibility (ENG-1810 → ENG-1815 / ENG-1816 / ENG-2000).
 *
 * This is the ONE place camera-ui names the backend fields and endpoint
 * behind the View Only screen picker. The names below are the contract
 * confirmed by actuate_admin #2959 (ENG-1814), plus the Patrols / WireGuard
 * extension proposed on ENG-1999; if one ever changes, change the constant
 * and nothing else has to move.
 *
 * Reading these values in the SPA only decides what to draw. What a user may
 * actually read or write is decided by actuate_admin and the monitoring API.
 */
import { createApi } from "./createApi";

/**
 * `AccessLevel.VIEW_ONLY` in actuate_admin. Access levels are NOT a ladder —
 * View Only is 5 but the least privileged — so always compare with `===`
 * through `isViewOnly`, never with `>=`.
 */
export const VIEW_ONLY_ACCESS_LEVEL = 5;

/**
 * `AccessLevel.ALARM_REVIEW` (ENG-2010): View Only plus setting alert
 * outcomes. admin puts these users in the View Only group too, so every
 * View Only rule (read-only UI, visible screens) applies to them; the
 * outcome buttons follow `permission.set_alert_outcome` separately.
 */
export const ALARM_REVIEW_ACCESS_LEVEL = 6;

/**
 * Field carrying the screen list. Read from `/user/info/` `permission` for
 * the logged-in user and from a `group_user/` row for the user being edited;
 * written back on `PATCH group_user/{id}/` and `POST group_user/`.
 * Empty / absent = View All.
 */
export const VISIBLE_SCREENS_FIELD = "visible_screens";

/**
 * Field on a `group_user/` row saying whether the TARGET user has CHM sites,
 * which decides whether "Health" is offered for them. Judged over the groups
 * that row grants, with the same helper as the logged-in user's own
 * `permission.chm` (actuate_admin #2959).
 */
export const TARGET_USER_CHM_FIELD = "chm";

/**
 * Same idea for Patrols and WireGuard (ENG-1999 / ENG-2000): booleans on the
 * `group_user/` row saying whether the TARGET user's groups have patrol sites
 * / WireGuard tunnels. Pending on the backend; absent = not offered.
 */
export const TARGET_USER_PATROL_FIELD = "patrol";
export const TARGET_USER_WIREGUARD_FIELD = "wireguard";

/**
 * Which row field must be true before a screen is offered in the picker.
 * Screens not listed here (Sites, Alerts, Analytics) are always offered.
 */
export const SCREEN_AVAILABILITY_FIELDS: Readonly<
  Partial<Record<string, string>>
> = {
  health: TARGET_USER_CHM_FIELD,
  patrols: TARGET_USER_PATROL_FIELD,
  wireguard: TARGET_USER_WIREGUARD_FIELD,
};

/**
 * Vocabulary endpoint, mirroring the existing `group_user/access_level/`
 * drop-down list. Labels come from here so the SPA does not keep a second
 * copy of the backend's list. Returns `[{id, name}]`; "View All" is not in it
 * (it is the empty selection) — the SPA adds that choice itself.
 */
export const VISIBLE_SCREENS_VOCABULARY_ENDPOINT =
  "group_user/visible_screens/";

/** Screen keys the nav and routes gate on. Labels are the backend's. */
export type ScreenKey =
  | "sites"
  | "alerts"
  | "analytics"
  | "health"
  | "patrols"
  | "wireguard";

export interface ScreenOption {
  key: string;
  label: string;
}

/**
 * UI-only sentinel for the empty list. It is never sent to the backend and is
 * not part of the backend vocabulary.
 */
export const VIEW_ALL_OPTION: ScreenOption = {
  key: "__view_all__",
  label: "View All",
};

export const isViewOnly = (accessLevel: unknown): boolean =>
  Number(accessLevel) === VIEW_ONLY_ACCESS_LEVEL ||
  Number(accessLevel) === ALARM_REVIEW_ACCESS_LEVEL;

/**
 * Screen list from any object carrying the field (a `/user/info/`
 * `permission` dict, the cached `userPermissions`, or a `group_user/` row).
 * Anything malformed reads as empty, i.e. View All.
 */
export const readVisibleScreens = (source: unknown): string[] => {
  const raw = (source as Record<string, unknown> | null | undefined)?.[
    VISIBLE_SCREENS_FIELD
  ];
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (key): key is string => typeof key === "string" && key.length > 0
  );
};

/** Empty list = View All; otherwise the screen must be listed. */
export const isScreenVisible = (
  visibleScreens: string[],
  screen: ScreenKey
): boolean => visibleScreens.length === 0 || visibleScreens.includes(screen);

export const targetUserHasChm = (groupUserRow: unknown): boolean =>
  screenAvailableForTargetUser(groupUserRow, "health");

/**
 * Whether a screen may be offered for the user a `group_user/` row describes:
 * Health needs CHM sites, Patrols needs patrol sites, WireGuard needs tunnels.
 * A screen with no availability field is always offered. Fails closed when
 * the row lacks the field (e.g. before the backend ships it).
 */
export const screenAvailableForTargetUser = (
  groupUserRow: unknown,
  screen: string
): boolean => {
  const field = SCREEN_AVAILABILITY_FIELDS[screen];
  if (field === undefined) return true;
  return (
    (groupUserRow as Record<string, unknown> | null | undefined)?.[field] ===
    true
  );
};

/** The backend vocabulary narrowed to what the target user can actually use. */
export const selectableScreens = (
  vocabulary: ScreenOption[],
  groupUserRow: unknown
): ScreenOption[] =>
  vocabulary.filter((option) =>
    screenAvailableForTargetUser(groupUserRow, option.key)
  );

/**
 * Resolve one change in the multi-select. `previous` is the saved list (empty
 * = View All), `nextKeys` is what the control now holds, possibly including
 * the View All sentinel.
 *
 * - Picking View All while specific screens are chosen clears them.
 * - Picking a specific screen while on View All drops View All.
 * - Ending up with nothing falls back to View All (empty list).
 */
export const applyScreenSelection = (
  previous: string[],
  nextKeys: string[]
): string[] => {
  const wasViewAll = previous.length === 0;
  const pickedViewAll = nextKeys.includes(VIEW_ALL_OPTION.key);
  const specific = nextKeys.filter((key) => key !== VIEW_ALL_OPTION.key);
  if (pickedViewAll && !wasViewAll) return [];
  return specific;
};

/**
 * The screen list only travels with View Only. For every other access level
 * the field is left out of the payload entirely (not sent as empty), so a
 * level change never touches it.
 */
export const withVisibleScreensForLevel = <T extends Record<string, unknown>>(
  payload: T,
  accessLevel: unknown
): T => {
  if (isViewOnly(accessLevel)) return payload;
  if (!(VISIBLE_SCREENS_FIELD in payload)) return payload;
  const rest: Record<string, unknown> = { ...payload };
  delete rest[VISIBLE_SCREENS_FIELD];
  return rest as T;
};

/**
 * Accept the shapes the vocabulary endpoint might reasonably use:
 * `[{id, name}]` (what `group_user/access_level/` returns today),
 * `[{key, label}]`, DRF choices `[{value, display_name}]`, plain strings, or
 * any of those under `results`.
 */
export const normalizeScreenVocabulary = (data: unknown): ScreenOption[] => {
  const rows: unknown[] = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
    ? ((data as { results: unknown[] }).results as unknown[])
    : [];
  const options: ScreenOption[] = [];
  rows.forEach((row) => {
    if (typeof row === "string") {
      if (row) options.push({ key: row, label: row });
      return;
    }
    const record = (row ?? {}) as Record<string, unknown>;
    const key = record.key ?? record.id ?? record.value;
    if (typeof key !== "string" || !key) return;
    const label = record.label ?? record.name ?? record.display_name ?? key;
    options.push({ key, label: String(label) });
  });
  return options;
};

export const fetchScreenVocabulary = async (): Promise<ScreenOption[]> => {
  const response = await createApi("").get(VISIBLE_SCREENS_VOCABULARY_ENDPOINT);
  return normalizeScreenVocabulary(response.data);
};

/**
 * Short, user-visible description of a failed vocabulary request, e.g.
 * "HTTP 404", "HTTP 403", or "network error". Shown under the picker so an
 * empty option list is never silent (the production logger is muted).
 */
export const describeVocabularyError = (reason: unknown): string => {
  const status = (reason as { response?: { status?: unknown } } | undefined)
    ?.response?.status;
  if (typeof status === "number") return `HTTP ${status}`;
  const message = (reason as { message?: unknown } | undefined)?.message;
  return typeof message === "string" && message ? message : "network error";
};

/**
 * Where a user lands after login, and where a hidden screen's route sends
 * them (ENG-1816). Walks the screens in nav order and returns the first one
 * that passes both its existing gate and the visible-screens list, so a user
 * scoped to Health lands on /health instead of an empty Sites page. `null`
 * means nothing is visible; the caller shows the no-permissions page rather
 * than redirecting (a redirect to a hidden route would loop).
 *
 * `permission` is the `/user/info/` permission dict or the cached
 * `userPermissions` built from it. `options` carries the gates that live
 * outside `/user/info/`: the WireGuard page is behind a LaunchDarkly flag.
 */
export interface LandingRouteOptions {
  wireguardPageEnabled?: boolean;
}

export const landingRouteFor = (
  permission: unknown,
  options: LandingRouteOptions = {}
): string | null => {
  const flags = (permission ?? {}) as Record<string, unknown>;
  if (flags.patrol_only) return "/sites_patrol";
  const screens = readVisibleScreens(flags);
  if (isScreenVisible(screens, "sites")) return "/dashboard";
  if (flags.view_alerts && isScreenVisible(screens, "alerts")) return "/alerts";
  // Clips rides the Alerts screen; it is the landing only when the user has a
  // clips source but not the alerts permission itself.
  if (
    (flags.ailink || flags.sentinel || flags.smtp) &&
    isScreenVisible(screens, "alerts")
  ) {
    return "/clips";
  }
  if (flags.view_analytics && isScreenVisible(screens, "analytics")) {
    return "/analytics";
  }
  if (flags.chm && isScreenVisible(screens, "health")) return "/health";
  if (flags.view_patrol && isScreenVisible(screens, "patrols")) {
    return "/sites_patrol";
  }
  if (options.wireguardPageEnabled && isScreenVisible(screens, "wireguard")) {
    return "/wireguardtunnels";
  }
  return null;
};
